import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { Close } from '@components/atoms';
import type { ClassNameProp } from '@utils';
import { cn } from '@utils';

// Main Dialog component from Radix UI
const Dialog = DialogPrimitive.Root;

// Component to trigger the dialog's visibility
const DialogTrigger = DialogPrimitive.Trigger;

// Portal component for rendering the dialog into a different part of the DOM
const DialogPortal = DialogPrimitive.Portal;

// Component for closing the dialog
const DialogClose = DialogPrimitive.Close;

/**
 * Overlay component for the dialog, providing a backdrop.
 */
const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<div>
		<DialogPrimitive.Overlay
			ref={ref}
			className={cn(
				'fixed inset-0 z-999 bg-bgOverlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
				className
			)}
			{...props}
		/>
	</div>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Non-modal overlay component for the dialog, providing a backdrop.
 */
const DialogOverlayNonModal = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<div
		className={cn(
			'fixed inset-0 z-999 bg-bgOverlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0'
		)}
		ref={ref}
		{...props}
	/>
));
DialogOverlayNonModal.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentPrimitiveProps = React.PropsWithChildren<
	DialogPrimitive.DialogContentProps &
		React.RefAttributes<HTMLDivElement> &
		ClassNameProp & {
			backButtonComponent?: React.ReactNode;
			closeIcon?: boolean;
			modal?: boolean;
		}
>;

const DialogContentPrimitive = React.forwardRef<HTMLDivElement, DialogContentPrimitiveProps>(({ ...props }, ref) => (
	<DialogPrimitive.Content {...props} ref={ref} />
));
DialogContentPrimitive.displayName = 'DialogContentPrimitive';

/**
 * Content component for the dialog, where the main content of the dialog is placed.
 */
const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogContentPrimitive>,
	React.ComponentPropsWithoutRef<typeof DialogContentPrimitive>
>(({ backButtonComponent, closeIcon, children, className, modal = true, ...props }, ref) => {
	const Overlay = modal ? DialogOverlay : DialogOverlayNonModal;
	return (
		<DialogPortal>
			<Overlay>
				<DialogContentPrimitive
					data-testid="dialog-content"
					aria-modal={modal || undefined}
					ref={ref}
					className={cn(
						'fixed top-[50%] left-[50%] z-999 flex w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 bg-white pt-8 duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
						className
					)}
					{...props}
					data-dialog-portal-container=""
				>
					{Boolean(backButtonComponent) && (
						<div className="ring-offset-background focus:ring-ring data-[state=open]:text-muted-foreground absolute top-4 left-4 rounded-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent">
							{backButtonComponent}
						</div>
					)}
					{children}
					{Boolean(closeIcon) && (
						<DialogClose className="ring-offset-background focus:ring-ring data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent">
							<Close className="h-4 w-4 text-secondary transition-all hover:text-primary" />
							<span className="sr-only">Close</span>
						</DialogClose>
					)}
				</DialogContentPrimitive>
			</Overlay>
		</DialogPortal>
	);
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Header component for the dialog, typically containing the title or other header content.
 */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-col space-y-2 px-10 text-center', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

/**
 * Footer component for the dialog, typically containing actions related to the dialog
 */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-row space-x-4 px-10 py-4', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

/**
 * Title component for the dialog, providing a large, bold text header.
 */
const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title ref={ref} className={cn('scroll-m-20 text-xl font-bold text-primary', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

/**
 * Description component for the dialog, typically used for a subtitle or additional text.
 */
const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description ref={ref} className={cn('text-xs font-normal text-textMediumGray', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogClose,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
	DialogContentPrimitiveProps,
};
